import { useCloseModal } from 'state/modal';
import React, { useCallback, useState } from 'react';
import { DOCUMENT_TYPES, UploadDocumentsRequest, GET_VEHICLE_DOCUMENTS_KEY, DocumentType } from 'api/document';
import { namesStrategy } from 'api/document/document.adapter';
import { Option } from 'common/form/select/select.types';
import { uuid } from 'global/utils/common';
import { useMutation, useQueryClient } from 'react-query';
import { api } from 'api/api';
import { onError } from 'global/utils/response/error-handler';
import { showToastSuccess, showToastError } from 'global/utils/toast';
import { AttachDocsContentProps } from './attach-docs-content.types';

type Doc = { file: File; id: string; option: Option };

export const useAttachDocsContent = (props: AttachDocsContentProps) => {
  const { id, isVehicularCheckPoint, onClose, uniqueId } = props;
  const { closeModal } = useCloseModal();
  const client = useQueryClient();
  const [documents, setDocuments] = useState<Doc[]>([]);
  const [error, setError] = useState<string | undefined | null>(null);

  const documentTypesAvailable = React.useMemo(
    () =>
      DOCUMENT_TYPES.filter(docType =>
        isVehicularCheckPoint
          ? ['padron', 'homologado', 'seguro_obligatorio', 'permiso_circulacion', 'revision_tecnica'].includes(docType)
          : !['tarjeta_vehicular', 'porta_dox'].includes(docType),
      )
        .map(docType => ({
          label: namesStrategy[docType].name,
          value: docType,
        }))
        .sort((doc1, doc2) => doc1.label.localeCompare(doc2.label)),
    [isVehicularCheckPoint],
  );

  const closeFn = useCallback(() => {
    onClose?.();
    closeModal(id as string);
  }, []);

  const { isLoading: isUploading, mutate } = useMutation(
    async (request: UploadDocumentsRequest) => api.document.uploadDocuments(request),
    {
      onError,
      onSuccess: async response => {
        if (!response.hasError()) {
          await client.invalidateQueries([GET_VEHICLE_DOCUMENTS_KEY, uniqueId]);
          showToastSuccess('Documento(s) adjuntado(s) correctamente.');
        }

        closeFn();
      },
    },
  );

  const confirmFn = useCallback(() => {
    if (!documents?.length) {
      setError('El tipo documental es requerido');
      return showToastError('Debe adjuntar al menos un documento.');
    }

    mutate({ documents, unique_id: uniqueId! });
  }, [documents]);

  const onAddAttach = useCallback(
    (file: File, type: Option) => {
      try {
        isValidFile(file);
        isValidFileType(file);
        isValidFileName(documents, file);

        const doc: Doc = { file, id: uuid(), option: type };

        if (isVehicularCheckPoint && (type.value as DocumentType) === 'revision_tecnica') {
          setDocuments(docs => [...docs.filter(d => d.option.value !== 'homologado'), doc]);
          return;
        }

        setError(null);
        setDocuments(docs => [...docs, doc]);
      } catch (e: any) {
        showToastError(e.message);
      }
    },
    [isVehicularCheckPoint, documents],
  );

  const onDeleteDocument = useCallback((documentId: string) => {
    setDocuments(docs => docs.filter(doc => doc.id !== documentId));
  }, []);

  return {
    closeFn,
    confirmFn,
    documentTypesAvailable,
    documents,
    errorMessage: error,
    isUploading,
    onAddAttach,
    onDeleteDocument,
  };
};

const isValidFile = (file: File) => {
  if (!file) {
    throw Error('Debe seleccionar un documento.');
  }

  return true;
};

const isValidFileType = (file: File) => {
  if (file.type !== 'application/pdf') {
    throw Error('Solo se permite adjuntar ficheros pdf.');
  }

  return true;
};

const isValidFileName = (existingDocuments: Doc[], file: File) => {
  if (existingDocuments?.some(doc => doc.file.name === file.name)) {
    throw Error('No se permiten documentos con el mismo nombre.');
  }

  return true;
};
